import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Paper,
  Slide,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import CallIcon from '@mui/icons-material/Call';
import CallEndIcon from '@mui/icons-material/CallEnd';
import PersonIcon from '@mui/icons-material/Person';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { PhoneCall } from '../types';

// ============================================================================
// INCOMING CALL NOTIFICATION - Persistent In-App Bildirim
// ============================================================================
//
// Hibrit telefon-arama sisteminin "modal açık" varyasyonu. Kasiyer bir modal
// (yeni müşteri kaydı, ödeme, hızlı satış vb.) ile çalışırken arama geldiğinde
// ekrana hâkim olan `IncomingCallPopupModal` yerine bu component sağ üst köşede
// kalıcı (auto-dismiss YOK) bir bildirim olarak gösterilir.
//
// KUYRUK DESTEĞİ (Q5):
// - `stackIndex` ile birden fazla bildirim alt alta stack edilebilir
// - `isPending` true ise "BEKLEYEN ARAMA" gösterimi (sarı renk, daha yumuşak pulse)
// - Aktif bildirimle birebir görsel tutarlı, sadece başlık ve renk farklı
//
// BİRLEŞİK KUYRUK (held aramalar):
// - `isHeld` true ise başlık "SIRADA BEKLEYEN MÜŞTERİ" olur ve yeşil buton
//   "Müşteriye Geç" aksiyonuna dönüşür (caller karar modalını açar).
// - `ringEnded` true ise "Zil sonlandı" bilgisi gösterilir — arama fiziksel
//   olarak bitmiş olsa da müşteri kuyrukta işlem bekliyor demektir.
//
// API: Eski `IncomingCallPopupModal` props'ları korunur — drop-in switch.
// ============================================================================

// Yaklaşık bildirim yüksekliği (px) — header + body + actions
const NOTIFICATION_APPROX_HEIGHT = 220;
const STACK_GAP = 8;
const STACK_TOP_OFFSET = 16;

export interface IncomingCallNotificationProps {
  open: boolean;
  incomingCall: PhoneCall | null;
  themeColor: string;
  onAnswer: () => void;
  onReject?: () => void;
  /** Stack içindeki konum (0 = en üstte). Default 0. */
  stackIndex?: number;
  /** Pending (bekleyen) arama mı? Default false (aktif görünüm) */
  isPending?: boolean;
  /** Pending sayısı (örn. "BEKLEYEN ARAMA #2"). Default 1. */
  pendingNumber?: number;
  /** Birleşik kuyruk: held arama mı? (SIRADA BEKLEYEN MÜŞTERİ görünümü) */
  isHeld?: boolean;
  /** Zil sinyali kesildi mi? (arama fiziksel olarak sonlanmış olabilir) */
  ringEnded?: boolean;
}

const IncomingCallNotification: React.FC<IncomingCallNotificationProps> = ({
  open,
  incomingCall,
  themeColor,
  onAnswer,
  onReject,
  stackIndex = 0,
  isPending = false,
  pendingNumber = 1,
  isHeld = false,
  ringEnded = false,
}) => {
  // Renkler — pending için sarı/turuncu, aktif için yeşil
  const headerColor = isPending ? '#FF9800' : '#4CAF50';
  const borderColor = isPending ? '#FF9800' : '#4CAF50';
  const shadowRgb = isPending ? '255, 152, 0' : '76, 175, 80';
  const pulseDuration = isPending ? '2.5s' : '1.5s';
  const animationName = isPending ? `pendingPulse_${stackIndex}` : `incomingPulse_${stackIndex}`;

  const topOffset = STACK_TOP_OFFSET + stackIndex * (NOTIFICATION_APPROX_HEIGHT + STACK_GAP);

  const title = isPending
    ? (isHeld ? `SIRADA BEKLEYEN MÜŞTERİ #${pendingNumber}` : `BEKLEYEN ARAMA #${pendingNumber}`)
    : 'GELEN ARAMA';

  // Held aramada yeşil buton "Yanıtla" değil "Müşteriye Geç" anlamındadır
  const answerLabel = isPending && isHeld ? 'Müşteriye Geç' : 'Yanıtla';

  return (
    <Slide direction="left" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={12}
        sx={{
          position: 'fixed',
          top: topOffset,
          right: 16,
          width: 360,
          maxWidth: 'calc(100vw - 32px)',
          zIndex: 1400 - stackIndex, // Aktif bildirim her zaman en üstte (z-order)
          borderRadius: 3,
          overflow: 'hidden',
          border: `2px solid ${borderColor}`,
          boxShadow: `0 8px 32px rgba(${shadowRgb}, 0.35)`,
        }}
      >
        {/* Pulse'lı başlık şeridi — aktif için yeşil, pending için sarı */}
        <Box
          sx={{
            bgcolor: headerColor,
            color: 'white',
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            animation: `${animationName} ${pulseDuration} infinite`,
            [`@keyframes ${animationName}`]: {
              '0%': { opacity: 1 },
              '50%': { opacity: isPending ? 0.85 : 0.75 },
              '100%': { opacity: 1 },
            },
          }}
        >
          <PhoneIcon sx={{ fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
            {title}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {incomingCall?.time || '--:--'}
          </Typography>
        </Box>

        {/* İçerik: telefon numarası + müşteri adı */}
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{ mb: 1, color: themeColor, wordBreak: 'break-all' }}
          >
            {incomingCall?.phone || 'Bilinmeyen Numara'}
          </Typography>

          {incomingCall?.customerName ? (
            <Chip
              icon={<PersonIcon />}
              label={incomingCall.customerName}
              color="primary"
              size="small"
              sx={{ fontSize: '0.85rem', maxWidth: '100%' }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              Kayıtlı olmayan numara
            </Typography>
          )}

          {isPending && isHeld && ringEnded && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.75, color: '#E65100', fontWeight: 'bold' }}
            >
              Zil sonlandı — müşteri sırada bekliyor
            </Typography>
          )}
        </Box>

        {/* Aksiyonlar: Reddet (kırmızı) + Yanıtla (yeşil) */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            pb: 2,
            pt: 0.5,
          }}
        >
          {onReject && (
            <IconButton
              onClick={onReject}
              aria-label="Reddet"
              sx={{
                bgcolor: '#F44336',
                color: 'white',
                width: 52,
                height: 52,
                '&:hover': { bgcolor: '#D32F2F' },
                boxShadow: '0 4px 12px rgba(244, 67, 54, 0.4)',
              }}
            >
              <CallEndIcon sx={{ fontSize: 28 }} />
            </IconButton>
          )}
          <IconButton
            onClick={onAnswer}
            aria-label={answerLabel}
            title={answerLabel}
            sx={{
              bgcolor: '#4CAF50',
              color: 'white',
              width: 52,
              height: 52,
              '&:hover': { bgcolor: '#388E3C' },
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
            }}
          >
            {isPending && isHeld ? (
              <SwapHorizIcon sx={{ fontSize: 28 }} />
            ) : (
              <CallIcon sx={{ fontSize: 28 }} />
            )}
          </IconButton>
        </Box>
      </Paper>
    </Slide>
  );
};

export default IncomingCallNotification;
